// WhatsApp configuration
const serviceNames: Record<string, string> = {
  'sanitise': 'Sanitasi',
  'pest-control': 'Kawalan Serangga',
  'fabric': 'Cuci Fabrik',
  'car-polish': 'Polish Kereta',
  'aircond': 'Servis Penghawa Dingin'
};

interface ServiceWithPrice {
  title: string;
  price: string;
}

// Helper to extract price info from string like "RM30" or range like "RM15~30"
function extractPriceInfo(price: string): { min: number; max: number; isRange: boolean } {
  // Check for range price first (e.g., "RM15~30")
  const rangeMatch = price.match(/RM([\d.]+)~([\d.]+)/i);
  if (rangeMatch) {
    return {
      min: parseFloat(rangeMatch[1]),
      max: parseFloat(rangeMatch[2]),
      isRange: true
    };
  }

  // Single price (e.g., "RM30")
  const match = price.match(/RM([\d.]+)/i);
  const priceNum = match ? parseFloat(match[1]) : 0;
  return { min: priceNum, max: priceNum, isRange: false };
}

const formatServiceLine = (service: { title: string; price: string; quantity?: number }) => {
  const qty = service.quantity ?? 1;
  const priceInfo = extractPriceInfo(service.price);

  if (priceInfo.isRange) {
    // For range prices, show the range with quantity
    const minTotal = priceInfo.min * qty;
    const maxTotal = priceInfo.max * qty;
    return qty > 1
      ? `${service.title} x${qty} - RM${minTotal.toFixed(2)}~${maxTotal.toFixed(2)}`
      : `${service.title} - RM${priceInfo.min.toFixed(2)}~${priceInfo.max.toFixed(2)}`;
  }

  // Single price
  const total = priceInfo.min * qty;
  return qty > 1
    ? `${service.title} x${qty} - RM${total.toFixed(2)}`
    : `${service.title} - RM${priceInfo.min.toFixed(2)}`;
};

export const whatsappConfig = {
  // Use a hardcoded phone number for now
  phoneNumber: '60182331065',
  
  // Format functions for different message types
  formatMessage: {
    // Booking message formatter
    booking: ({
      name,
      date,
      address,
      selectedServices,
      totalPrice,
      mainService
    }: {
      name: string;
      date: string;
      address: string;
      mainService?: string;
      selectedServices: Set<ServiceWithPrice>;
      totalPrice: string;
    }) => {
      const services = Array.from(selectedServices)
        .filter(s => !s.title.includes('Caj Pengangkutan'))
        .map(formatServiceLine)
        .join('\n');
      
      const transport = Array.from(selectedServices)
        .find(s => s.title.includes('Caj Pengangkutan'));

      const serviceName = mainService ? serviceNames[mainService] || mainService : '';

      return `
*TRHINE SERVICES - TEMPAHAN BARU*
Nama: ${name}
Tarikh: ${date}
Alamat: ${address}

*Perkhidmatan: ${serviceName}*
${services}
${transport ? `\n${transport.title} - ${transport.price}` : ''}

*Jumlah: ${totalPrice}*
      `.trim();
    },
    
    // Contact form message formatter
    contact: (data: {
      name: string;
      question: string;
    }) => {
      return `*TRHINE SERVICES - HOTLINE*\n\n` +
        `*Nama:*\n${data.name}\n\n` +
        `*Pertanyaan:*\n${data.question}`;
    }
  },
  
  // Helper function to open WhatsApp with formatted message
  openWhatsApp: (message: string) => {
    // Properly encode special characters and preserve formatting
    const encodedMessage = encodeURIComponent(message)
      .replace(/'/g, '%27')
      .replace(/"/g, '%22')
      .replace(/\(/g, '%28')
      .replace(/\)/g, '%29')
      .replace(/\*/g, '*');  // Keep asterisks for WhatsApp formatting
    
    window.open(`https://wa.me/${whatsappConfig.phoneNumber}?text=${encodedMessage}`, '_blank');
  }
};

export const formatWhatsAppMessage = (serviceId: string, selectedServices: ServiceWithPrice[]) => {
  const serviceName = serviceNames[serviceId] || serviceId;
  
  let message = `Saya berminat untuk menggunakan perkhidmatan ${serviceName}.\n\n`;
  
  if (selectedServices.length > 0) {
    message += "Perkhidmatan yang dipilih:\n";
    selectedServices.forEach(service => {
      // For aircond services, the unit count is already included in the title
      message += `- ${service.title}: ${service.price}\n`;
    });
  }

  return encodeURIComponent(message);
}; 